import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, UserPlus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import {
  ROLES_COUNT,
  type ActiveUser,
  type DeviceGroup,
} from "../../data";
import { createUser, listGroups, listUsers } from "../../api/endpoints";
import { toActiveUser, toDeviceGroup } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import { ApiError } from "../../api/types";

const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "user",        label: "Пользователь" },
  { value: "dev",         label: "Разработчик" },
  { value: "local_admin", label: "Локальный администратор" },
  { value: "admin",       label: "Глобальный администратор" },
];

export default function UsersPage() {
  const qc = useQueryClient();
  const push = useToast();

  // Дефолт — "ALL" (все пользователи), т.к. группа OFFICE может не существовать.
  const [groupFilter, setGroupFilter] = useState<string>("ALL");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ActiveUser | null>(null);

  const groupsQ = useQuery({ queryKey: ["groups"], queryFn: listGroups });
  const usersQ = useQuery({
    queryKey: ["users", groupFilter],
    queryFn: () => listUsers(groupFilter === "ALL" ? undefined : groupFilter),
  });

  // Пустой fallback (не mockGroups) — иначе селект показывает несуществующие коды.
  const groups = useApiFallback<DeviceGroup[]>(
    groupsQ.data?.map(toDeviceGroup),
    groupsQ.isError,
    [],
  );

  const apiUsers: ActiveUser[] | undefined = usersQ.data?.map(toActiveUser);
  // Пустой fallback — иначе при reload мелькают демо-пользователи.
  const users = useApiFallback<ActiveUser[]>(apiUsers, usersQ.isError, []);

  // q-фильтр на клиенте (бэк не принимает поиск по пользователям)
  const filtered = useMemo(() => {
    if (!q.trim()) return users.value;
    const t = q.toLowerCase();
    return users.value.filter(
      (u) =>
        u.fullName.toLowerCase().includes(t) ||
        u.login.toLowerCase().includes(t) ||
        u.role.toLowerCase().includes(t),
    );
  }, [users.value, q]);

  const totalDevices = groups.value.find((g) => g.code === "ALL")?.devices ?? 0;

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users"] });
      await qc.invalidateQueries({ queryKey: ["groups"] });
      push({ kind: "success", text: "Учётная запись создана" });
      setCreateOpen(false);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось создать") : "Не удалось создать";
      push({ kind: "warn", text: msg });
    },
  });

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Пользователи и группы
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {totalDevices.toLocaleString("ru-RU")} пользователей · {groups.value.length} групп · {ROLES_COUNT} роли
          </div>
        </div>
        <Btn icon={Plus} variant="primary" onClick={() => setCreateOpen(true)}>
          Создать учётную запись
        </Btn>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
        {/* Левая колонка — карточки групп */}
        <section className="space-y-2">
          <h2 className="px-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-faint)]">
            Группы устройств
          </h2>
          <div className={"flex flex-col gap-2 " + (groupsQ.isLoading ? "opacity-60" : "")}>
            {groups.value.map((g) => (
              <GroupCard
                key={g.code}
                group={g}
                active={groupFilter === g.code}
                onClick={() => setGroupFilter(g.code)}
              />
            ))}
          </div>
        </section>

        {/* Правая колонка — пользователи группы */}
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">
                {groups.value.find((g) => g.code === groupFilter)?.name ?? "Пользователи"}
              </h2>
              <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
                {filtered.length} {pluralize(filtered.length, ["пользователь", "пользователя", "пользователей"])} в этой группе
              </p>
            </div>
            <div className="relative w-full sm:w-[280px]">
              <Search
                size={13}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]"
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск пользователя"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1.5 pl-7 pr-3 text-[12.5px] text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
              />
            </div>
          </div>

          <div className={"-mx-1 mt-4 overflow-x-auto " + (usersQ.isLoading ? "opacity-60" : "")}>
            <table className="w-full min-w-[640px] text-[12.5px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                  <Th>Пользователь</Th>
                  <Th>Логин</Th>
                  <Th>Роль</Th>
                  <Th>Группа</Th>
                  <Th>Устройство</Th>
                  <Th>Был онлайн</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => (
                  <tr
                    key={u.login}
                    style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
                    className="stagger-up cursor-pointer border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface-2)]"
                    onClick={() => setSelectedUser(u)}
                  >
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--color-surface-3)] text-[10.5px] font-medium text-[var(--color-fg)]">
                          {u.initials}
                        </span>
                        {u.fullName}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{u.login}</td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <RoleChip role={u.role} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono">{u.group}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-[var(--color-fg-muted)]">{u.device}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-[var(--color-fg-muted)]">{u.online}</td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                      В этой группе пока никого нет.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        groups={groups.value}
        defaultGroup={groupFilter}
        onSubmit={(payload) => createMut.mutate(payload)}
        pending={createMut.isPending}
      />

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </>
  );
}

function UserDetailModal({ user, onClose }: { user: ActiveUser; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={user.fullName}>
      <div className="flex flex-col gap-3 text-[13px]">
        <Row label="Логин" value={<span className="font-mono">{user.login}</span>} />
        <Row label="Роль" value={<RoleChip role={user.role} />} />
        <Row label="Группа" value={user.group || "—"} />
        <Row label="Устройство" value={user.device || "—"} />
        <Row label="Был онлайн" value={user.online || "—"} />
      </div>
      <div className="mt-5 flex justify-end">
        <Btn onClick={onClose}>Закрыть</Btn>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-[var(--color-fg-muted)]">{label}</span>
      <span className="text-[var(--color-fg)]">{value}</span>
    </div>
  );
}

function CreateUserModal({
  open,
  onClose,
  groups,
  defaultGroup,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  groups: DeviceGroup[];
  defaultGroup: string;
  onSubmit: (payload: {
    login: string;
    full_name: string;
    role: string;
    group_code: string | null;
    password: string;
  }) => void;
  pending: boolean;
}) {
  const [login, setLogin] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  // Дефолт — первая реально существующая группа (а не захардкоженный "OFFICE",
  // которого может не быть в БД). Если групп нет — пусто (group_code: null).
  // Дефолт — первая группа из списка (включая ALL)
  const [groupCode, setGroupCode] = useState<string>(groups[0]?.code ?? "");

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- сброс формы при открытии модалки
      setLogin("");
      setFullName("");
      setEmail("");
      setRole("user");
      setGroupCode(
        groups[0]?.code ?? "",
      );
    }
  }, [open, defaultGroup]);

  const canSubmit = login.trim().length >= 2 && fullName.trim().length >= 2 && !pending;

  // На бэке нет отдельного поля email и пароля — передаём логин/имя/роль/группу.
  // password обязателен в типе UserCreate, формируем разовый.
  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      login: login.trim(),
      full_name: fullName.trim(),
      role,
      group_code: groupCode || null,
      password: temporaryPassword(login.trim()),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <UserPlus size={16} className="text-[var(--color-brand)]" />
          Новая учётная запись
        </span>
      }
      subtitle="Сотрудник получит временный пароль на e-mail"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" onClick={submit}>
            {pending ? "Создание…" : "Создать"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3 text-[12.5px]">
        <Field label="Логин" hint="латиница, .">
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value.replace(/\s+/g, ""))}
            placeholder="ivanov.a"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="ФИО">
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Иванов Алексей Сергеевич"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="E-mail">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.ru"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Роль">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Группа">
            <select
              value={groupCode}
              onChange={(e) => setGroupCode(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
            >
              <option value="">— без группы —</option>
              {groups.map((g) => (
                <option key={g.code} value={g.code}>{g.code} · {g.name}</option>
              ))}
            </select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          {label}
        </span>
        {hint && <span className="text-[10.5px] text-[var(--color-fg-faint)]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function temporaryPassword(seed: string): string {
  // Просто детерминированная заготовка временного пароля для отправки на e-mail.
  const base = seed + "-tmp-" + Math.random().toString(36).slice(2, 8);
  return base;
}

function GroupCard({
  group,
  active,
  onClick,
}: {
  group: DeviceGroup;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-all duration-150 " +
        (active
          ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5 shadow-[0_4px_12px_-4px_rgba(31,58,138,0.18)]"
          : "border-[var(--color-border)] bg-[var(--color-surface)] hover:-translate-y-0.5 hover:border-[var(--color-fg-muted)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)]")
      }
    >
      <span
        className={
          "grid h-9 w-9 shrink-0 place-items-center rounded-lg font-mono text-[10.5px] font-semibold " +
          (active
            ? "bg-[var(--color-brand)] text-white"
            : "bg-[var(--color-surface-3)] text-[var(--color-fg)]")
        }
      >
        {group.letters}
      </span>
      <div className="min-w-0">
        <div className={"truncate text-[13px] font-medium " + (active ? "text-[var(--color-fg)]" : "text-[var(--color-fg)]")}>
          {group.name}
        </div>
        <div className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
          {group.devices.toLocaleString("ru-RU")} устройств · {group.appsVisible} приложений
        </div>
      </div>
    </button>
  );
}

function RoleChip({ role }: { role: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    "разработчик": { bg: "var(--color-warning-bg)", fg: "var(--color-warning)" },
    "пользователь": { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)" },
    "лок. админ":  { bg: "var(--color-brand)/15", fg: "var(--color-brand)" },
    "глоб. админ": { bg: "var(--color-danger-bg)", fg: "var(--color-danger)" },
  };
  const m = map[role] ?? { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)" };
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10.5px] font-medium"
      style={{ background: m.bg, color: m.fg }}
    >
      {role}
    </span>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function pluralize(n: number, forms: [string, string, string]) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
}
